import styled from "@emotion/styled";
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from "recharts";
import {
  SellerOrderStatusBuckets,
  SellerReportPeriod,
} from "services/interface/sellerReport.interface";
import { numberFormatInt } from "utils/nuberFormat";
import { useSellerOrderStatus } from "hooks/useSellerReport";
import SectionCard from "../shared/SectionCard";
import { ORDER_STATUS_COLORS, ORDER_STATUS_LABELS, sd } from "../tokens";

interface OrderStatusCardProps {
  period: SellerReportPeriod;
}

const BUCKET_ORDER: (keyof SellerOrderStatusBuckets)[] = [
  "awaiting_prep",
  "shipping",
  "delivered",
  "cancelled",
];

const OrderStatusCard = ({ period }: OrderStatusCardProps) => {
  const { data, isPending, isFetching, isError, refetch } = useSellerOrderStatus(period);

  const buckets = data?.buckets;
  const rows = BUCKET_ORDER.map((key) => ({
    key,
    name: ORDER_STATUS_LABELS[key],
    color: ORDER_STATUS_COLORS[key],
    value: buckets?.[key] ?? 0,
  }));
  const total = data?.total ?? 0;

  return (
    <SectionCard
      eyebrow="คำสั่งซื้อ"
      title="สถานะออเดอร์"
      minHeight={360}
      skeletonRows={6}
      loading={isPending}
      refreshing={isFetching && !isPending}
      error={isError ? true : undefined}
      onRetry={() => refetch()}
    >
      <Content>
        <DonutBox>
          {/* recharts ไม่วาดอะไรเลยถ้าทุก slice เป็นศูนย์ — วาดวงเทาเรียบแทน */}
          {total === 0 ? (
            <EmptyRing />
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={rows}
                  dataKey="value"
                  nameKey="name"
                  innerRadius="62%"
                  outerRadius="88%"
                  paddingAngle={2}
                  startAngle={90}
                  endAngle={-270}
                  stroke="none"
                >
                  {rows.map((row) => (
                    <Cell key={row.key} fill={row.color} />
                  ))}
                </Pie>
                <Tooltip
                  formatter={(value: number, name: string) => [`${numberFormatInt(value)} รายการ`, name]}
                  contentStyle={{ borderRadius: 8, borderColor: sd.border, fontSize: 13 }}
                />
              </PieChart>
            </ResponsiveContainer>
          )}
          <Center>
            <CenterValue>{numberFormatInt(total)}</CenterValue>
            <CenterLabel>ทั้งหมด</CenterLabel>
          </Center>
        </DonutBox>

        <Legend>
          {rows.map((row) => (
            <LegendRow key={row.key}>
              <Dot color={row.color} />
              <LegendName>{row.name}</LegendName>
              <LegendValue>{numberFormatInt(row.value)}</LegendValue>
            </LegendRow>
          ))}
        </Legend>
      </Content>

      <Footer>
        <FooterValue>{(data?.on_time_pct ?? 0).toFixed(1)}%</FooterValue>
        <span>จัดส่งสำเร็จตามกำหนด</span>
      </Footer>
    </SectionCard>
  );
};

export default OrderStatusCard;

const Content = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
`;

const DonutBox = styled.div`
  position: relative;
  width: 168px;
  height: 168px;
  flex: 0 0 auto;
  margin: 0 auto;
`;

const EmptyRing = styled.div`
  width: 100%;
  height: 100%;
  border-radius: 50%;
  border: 20px solid ${sd.border};
`;

const Center = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  pointer-events: none;
`;

const CenterValue = styled.div`
  font-size: 26px;
  font-weight: 700;
  color: ${sd.navy};
  line-height: 1.1;
`;

const CenterLabel = styled.div`
  font-size: 12px;
  color: ${sd.subtle};
`;

const Legend = styled.div`
  flex: 1 1 180px;
  min-width: 170px;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const LegendRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Dot = styled.span<{ color: string }>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex: 0 0 auto;
  background: ${(p) => p.color};
`;

const LegendName = styled.span`
  flex: 1;
  font-size: 13px;
  color: ${sd.muted};
`;

const LegendValue = styled.span`
  font-size: 14px;
  font-weight: 700;
  color: ${sd.ink};
`;

const Footer = styled.div`
  margin-top: 16px;
  padding: 10px 12px;
  border-radius: 8px;
  background: ${sd.greenSoft};
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  font-size: 13px;
  color: ${sd.muted};
`;

const FooterValue = styled.strong`
  color: ${sd.green};
  font-size: 14px;
`;
